import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { LoadingSpinner } from './LoadingSpinner';

interface WBSButtonProps {
  wide?: boolean;
  color?: string;
  alternate?: boolean;
  icon?: IconDefinition;
  isLoading?: boolean;
  children?: React.ReactNode;
  as?: 'button' | 'a' | React.ComponentType<any>;
  disabled?: boolean;
}

export const WBSButton = ({
  icon,
  isLoading,
  children,
  as = 'button',
  disabled,
  ...props
}: WBSButtonProps & Record<string, any>) => {
  const isButton = as === 'button';
  const isDisabled = disabled || isLoading;

  // For non-button elements (anchor, Link, etc.), mimic disabled behavior
  const handleNonButtonClick = (e: React.MouseEvent<any>) => {
    if (!isButton && isDisabled) {
      e.preventDefault();
      return;
    }
    // Call original onClick if provided
    if ('onClick' in props && props.onClick) {
      props.onClick(e);
    }
  };

  const buttonProps = isButton
    ? {
        ...props,
        disabled: isDisabled,
      }
    : {
        ...props,
        'aria-disabled': isDisabled,
        tabIndex: isDisabled ? -1 : undefined,
        onClick: handleNonButtonClick,
      };

  return (
    <StyledButton
      as={as}
      {...buttonProps}
      $hasIcon={!!icon}
      $isDisabled={isDisabled}
    >
      <ContentWrapper $isLoading={isLoading}>
        {icon && <ButtonIcon icon={icon} alternate={props.alternate} />}
        {children}
      </ContentWrapper>
      {isLoading && (
        <SpinnerWrapper>
          <LoadingSpinner />
        </SpinnerWrapper>
      )}
    </StyledButton>
  );
};

const StyledButton = styled.button<
  Omit<WBSButtonProps, 'icon'> & { $hasIcon?: boolean; $isDisabled?: boolean }
>`
  align-items: center;
  background-color: ${(props) => {
    if (props.$isDisabled) return 'var(--grey-300)';
    if (props.alternate) return 'transparent';
    return props.color || 'var(--wbs-red)';
  }};
  border: 1px solid
    ${(props) => {
      if (props.$isDisabled) return 'var(--grey-500)';
      if (props.alternate) return 'var(--wbs-red)';
      return 'transparent';
    }};
  border-radius: 4px;
  box-shadow: ${(props) =>
    props.$isDisabled ? 'none' : props.alternate ? 'none' : 'var(--shadow-sm)'};
  color: ${(props) =>
    props.$isDisabled
      ? 'var(--grey-500)'
      : props.alternate
        ? 'var(--wbs-red)'
        : 'var(--white)'};
  cursor: ${(props) => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  font-family: var(--raleway);
  font-size: ${(props) => (props.wide ? '11px' : '12px')};
  font-weight: ${(props) => (props.wide ? '400' : '500')};
  gap: 8px;
  justify-content: center;
  letter-spacing: 0.6px;
  opacity: ${(props) => (props.$isDisabled ? '1' : '1')};
  padding: ${(props) =>
    props.wide ? (props.$hasIcon ? '6px 17px' : '6px 25px') : '5px 10px'};
  pointer-events: ${(props) => (props.$isDisabled ? 'none' : 'auto')};
  position: relative;
  text-decoration: none;
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$isDisabled
        ? 'var(--grey-300)'
        : props.alternate
          ? 'var(--wbs-red)'
          : 'var(--white)'};
    border: 1px solid
      ${(props) => {
        if (props.$isDisabled) return 'var(--grey-500)';
        if (props.alternate) return 'var(--wbs-red)';
        return props.color || 'var(--wbs-red)';
      }};
    box-shadow: ${(props) => (props.$isDisabled ? 'none' : 'var(--shadow-sm)')};
    color: ${(props) => {
      if (props.$isDisabled) return 'var(--grey-500)';
      if (props.alternate) return 'var(--white)';
      return props.color || 'var(--wbs-red)';
    }};
    text-decoration: none;
  }

  &:disabled {
    background-color: var(--grey-300);
    border: 1px solid var(--grey-500);
    color: var(--grey-500);
    cursor: not-allowed;
    opacity: 1;
  }
`;

const ContentWrapper = styled.span<{ $isLoading?: boolean }>`
  align-items: center;
  display: inline-flex;
  gap: 8px;
  visibility: ${(props) => (props.$isLoading ? 'hidden' : 'visible')};
`;

const ButtonIcon = styled(FontAwesomeIcon)<{ alternate?: boolean }>`
  color: ${(props) => (props.alternate ? 'var(--wbs-red)' : 'inherit')};
  font-size: 14px;
  transition: color 0.2s ease-in-out;

  ${StyledButton}:hover:not(:disabled) & {
    color: ${(props) => (props.alternate ? 'var(--white)' : 'var(--wbs-red)')};
  }

  ${StyledButton}:disabled & {
    color: var(--grey-500);
  }
`;

const SpinnerWrapper = styled.span`
  align-items: center;
  display: flex;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const WBSFullWidthButton = styled(WBSButton)`
  font-family: var(--raleway);
  font-size: 14px;
  letter-spacing: 0.8px;
  padding: 5px;
  width: 100%;
`;

export const WBSLargeButton = styled(WBSButton)`
  box-shadow: none;
  font-family: var(--open-sans);
  font-size: 14px;
  font-weight: 400;
  padding: 12px;
  width: 100%;

  &:hover:not(:disabled) {
    box-shadow: none;
  }
`;

export const WBSTallButton = styled(WBSButton)`
  padding-bottom: 10px;
  padding-top: 10px;
`;
